import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, CheckCircle, AlertCircle, X, FileText, Loader } from 'lucide-react';
import { FaCloudUploadAlt } from 'react-icons/fa';
import {
  uploadResume,
  checkUploadResumeStatus
} from '../../services/profile';
import './ResumeUploader.css';

const ResumeUploader = ({ onProfileUpdate }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [resumeName, setResumeName] = useState('My Resume');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log(jobId);
    // If we have a job ID, poll for status
    if (jobId && processingStatus === 'processing') {
      const interval = setInterval(async () => {
        try {
          const response = await checkUploadResumeStatus(jobId);
          console.log(response);
          const { status, error } = response;
          
          setProcessingStatus(status);
          
          if (status === 'failed') {
            setError(error || 'Failed to process resume');
            // Call the profile update even on failure
            if (onProfileUpdate && typeof onProfileUpdate === 'function') {
              onProfileUpdate();
            }
          }
          
          if (status === 'completed') {
            // Call the profile update on success
            if (onProfileUpdate && typeof onProfileUpdate === 'function') {
              onProfileUpdate();
            }
          }
          
          if (status !== 'processing') {
            clearInterval(interval);
          }
        } catch (err) {
          setError('Error checking status');
          setProcessingStatus('failed');
          clearInterval(interval);
        }
      }, 2000); // Check every 2 seconds
      
      return () => clearInterval(interval);
    }
  }, [jobId, processingStatus, onProfileUpdate]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    if (!isUploading && processingStatus !== 'processing') {
      setIsModalOpen(false);
      setFile(null);
      setResumeName('My Resume');
      setUploadProgress(0);
      setProcessingStatus(null);
      setJobId(null);
      setError(null);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Use filename (without extension) as default resume name
      const fileName = selectedFile.name.split('.').slice(0, -1).join('.');
      if (fileName) {
        setResumeName(fileName);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) return;
    
    try {
      setIsUploading(true);
      setUploadProgress(0);
      setProcessingStatus('uploading');
      setError(null);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('resume_name', resumeName);
      
      const response = await uploadResume(formData);
      
      setJobId(response.job_id);
      setProcessingStatus('processing');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error uploading file');
      setProcessingStatus('failed');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <motion.button 
        className="action-button upload"
        onClick={openModal}
        whileHover={{ scale: 1.03, y: -2 }}
        whileTap={{ scale: 0.97 }}
      >
        <FaCloudUploadAlt /> Import Resume
      </motion.button>
      
      <AnimatePresence>
        {isModalOpen && (
          <div className="modal-backdrop"
            onClick={closeModal}
          >
            <motion.div className="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Upload Your Resume</h2>
                <button 
                  className="close-button"
                  onClick={closeModal}
                  disabled={isUploading || processingStatus === 'processing'}
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="modal-body">
                {!processingStatus && (
                  <form className="upload-form" onSubmit={handleSubmit}>
                    <div className="file-input-wrapper">
                      <label className="file-input-label" htmlFor="resumeFile">
                        {file ? (
                          <>
                            <FileText size={32} />
                            <span>{file.name}</span>
                          </>
                        ) : (
                          <>
                            <Upload size={32} />
                            <span>Drag & drop your resume or click to browse</span>
                            <small>Supported formats: PDF, DOCX</small>
                          </>
                        )}
                      </label>
                      <input
                        className="file-input"
                        type="file"
                        id="resumeFile"
                        accept=".pdf,.docx"
                        onChange={handleFileChange}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="resumeName">Resume Name</label>
                      <input
                        type="text"
                        id="resumeName"
                        value={resumeName}
                        onChange={(e) => setResumeName(e.target.value)}
                      />
                    </div>
                    
                    <button 
                      className="submit-button"
                      type="submit" 
                      disabled={!file || isUploading}
                    >
                      Upload Resume
                    </button>
                  </form>
                )}
                
                {processingStatus === 'uploading' && (
                  <div className="status-container">
                    <div className="status-icon">
                      <Loader className="animate-spin" size={48} />
                    </div>
                    <h3 className="status-title">Uploading your resume...</h3>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                    </div>
                    <p className="status-info">{uploadProgress}% complete</p>
                  </div>
                )}
                
                {processingStatus === 'processing' && (
                  <div className="status-container">
                    <div className="status-icon">
                      <Loader className="animate-spin" size={48} />
                    </div>
                    <h3 className="status-title">Processing your resume...</h3>
                    <p className="status-info">We're extracting information from your resume.</p>
                    <p className="status-info">This may take a moment.</p>
                  </div>
                )}
                
                {processingStatus === 'completed' && (
                  <div className="status-container">
                    <div className="status-icon success">
                      <CheckCircle size={48} />
                    </div>
                    <h3 className="status-title success">Resume Uploaded Successfully!</h3>
                    <p className="status-info">Your resume has been processed and is ready for use.</p>
                    <button className="done-button" onClick={closeModal}>Done</button>
                  </div>
                )}
                
                {processingStatus === 'failed' && (
                  <div className="status-container">
                    <div className="status-icon error">
                      <AlertCircle size={48} />
                    </div>
                    <h3 className="status-title error">Upload Failed</h3>
                    <p className="status-info error">{error || 'An error occurred while processing your resume.'}</p>
                    <button className="done-button" onClick={closeModal}>Close</button>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ResumeUploader;